import { useCallback, useEffect, useState } from 'react';
import First from 'components/quote/status/First';
import PageTemplate from 'components/template/PageTemplate';
import Container from 'components/common/Container';
import { jsonAxios } from 'components/axios';
import { IList, ISearchInfo } from 'types/quote';
import CustomSelect from 'components/common/CustomSelect';
import { convertStatus, quoteStatusCountSelect } from 'data/quote';
import { convertQuoteType, countryCodeToEmoji, pointCountry } from 'components/function';
import useCheckCompany from 'hook/useCheckCompany';
import { useNavigate } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { ReactComponent as Copy } from 'assets/common/copy.svg'
import { Tooltip } from 'react-tooltip';
import Pagination from 'components/common/Pagination';

const StatusList = () => {
    const [searchInfo, setSearchInfo] = useState<ISearchInfo>({
        fromDate: '',
        toDate: '',
        status: '',
        origin: '',
        destination: '',
        title: '',
        pageSize: 10,
        page: 1,
    });

    const [totalPage, setTotalPage] = useState<number>(0)
    const [totalCount, setTotalCount] = useState<number>(0)
    const [list, setList] = useState<IList[]>([]);
    const navigate = useNavigate();
    const tHeader = [
        { label: "견적 ID", flex: 1.5 },
        { label: "계정", flex: 1.5 },
        { label: "요청일", flex: 0.7 },
        { label: '구분', flex: 0.5 },
        { label: "상태", flex: 0.5 },
        { label: "출발국가", flex: 1.0 },
        { label: "도착국가", flex: 1.0 },
        { label: "화물정보", flex: 1.5 },
    ];

    useCheckCompany();

    const init = useCallback(() => {
        const params = new URLSearchParams(Object.entries(searchInfo)).toString();
        jsonAxios.get(`quote?${params}`).then(res => {
            if (res.data.code === 200) {
                setList(res.data.data);
                setTotalPage(res.data.totalPages || 0);
                setTotalCount(res.data.totalCount || 0);
            }
        });
    }, [searchInfo]);

    useEffect(() => {
        init();
    }, [init]);

    const tBodyStyle = () => {
        return `flex items-center justify-center py-2 text-center tablet:py-1 tablet:gap-2`
    }

    const titleStyle = () => {
        return `hidden tablet:block flex-[1.0] w-full bg-[#e5e5e5] py-1`
    }

    return (
        <PageTemplate background='bg-[#F8F8FA]'>
            <Container className="flex flex-col gap-6 tablet:gap-4 mb-6 tablet:py-4 pt-21 h-full min-h-dvh">
                <First searchInfo={searchInfo} setSearchInfo={setSearchInfo} init={init} />

                <div className='flex flex-col flex-1 gap-3'>
                    <div className='bg-white rounded-lg'>
                        <div className='flex tablet:justify-between items-center gap-6 px-10 tablet:px-4 border-b-1 w-full h-20 tablet:h-15'>
                            <p className='font-semibold tablet:text-lg text-2xl'>총 {totalCount}건</p>
                            <div className='w-[148px]'>
                                <CustomSelect
                                    height={40}
                                    fontSize={14}
                                    option={quoteStatusCountSelect}
                                    value={
                                        searchInfo.pageSize
                                            ? quoteStatusCountSelect.find(arr => arr.value === String(searchInfo.pageSize))
                                            : null
                                    }
                                    placeholder={'10개 보기'}
                                    onChange={(value) => {
                                        setSearchInfo(p => ({
                                            ...p,
                                            pageSize: Number(value),
                                            page: 1
                                        }))
                                    }}
                                />
                            </div>
                        </div>
                        <div className='flex flex-col gap-3 px-10 tablet:px-4 py-6 w-full'>
                            <div className='tablet:hidden flex bg-[#F8F8FA] font-semibold text-sm 2xl:text-base'>
                                {
                                    tHeader.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className={`flex-[${item.flex}] flex items-center justify-center h-15`}
                                        >
                                            {item.label}
                                        </div>
                                    ))
                                }
                            </div>

                            <div>
                                {
                                    list.length > 0 ?
                                        <div className='flex flex-col gap-3 min-h-[30vh]'>
                                            {
                                                list.map((item, idx) => (
                                                    <div className='relative' key={idx}>
                                                        <div
                                                            className={`flex text-sm border border-[#E1E1E8] transition-all cursor-pointer hover:bg-[#EEF4FF] ${item.isOpen ? 'bg-[#EEF4FF]' : ''} rounded  tablet:flex-col`}
                                                            onClick={() => {
                                                                navigate(`/quote/status/detail?id=${item.id}`)
                                                            }}>
                                                            <div
                                                                className={`flex-[1.5] ${tBodyStyle()} tablet:order-2 tablet:flex-col`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                }}
                                                            >
                                                                <p className={titleStyle()}>{tHeader[0].label}</p>
                                                                <CopyToClipboard
                                                                    text={item.quoteId}
                                                                    onCopy={() => toast.success("견적 ID가 복사되었습니다.")}
                                                                >
                                                                    <div className='flex items-center gap-1'>
                                                                        <p>{item.quoteId}</p>
                                                                        <div
                                                                            data-tooltip-id="my-tooltip"
                                                                            data-tooltip-content="견적 ID 클릭시 클립보드로 복사됩니다."
                                                                            data-tooltip-place="top"
                                                                        >
                                                                            <Tooltip id="my-tooltip" />
                                                                            <Copy fill='#9D9EB7' className='w-4' />
                                                                        </div>
                                                                    </div>
                                                                </CopyToClipboard>
                                                            </div>
                                                            <div className={`flex-[1.5] ${tBodyStyle()} tablet:order-3`}>
                                                                <p className={titleStyle()}>{tHeader[1].label}</p>
                                                                <p className='flex-1 tablet:truncate'>
                                                                    {item.username}
                                                                </p>
                                                            </div>
                                                            <div className={`flex-[0.7] ${tBodyStyle()} tablet:order-4`}>
                                                                <p className={titleStyle()}>{tHeader[2].label}</p>
                                                                <p className='flex-1'>
                                                                    {item.createAt}
                                                                </p>
                                                            </div>
                                                            <div className={`flex-[0.5] ${tBodyStyle()} tablet:order-5`}>
                                                                <p className={titleStyle()}>{tHeader[3].label}</p>
                                                                <p className='flex-1'>
                                                                    {convertQuoteType(item.quoteType)}
                                                                </p>
                                                            </div>
                                                            <div className={`flex-[0.5] ${tBodyStyle()} tablet:order-6`}>
                                                                <p className={titleStyle()}>{tHeader[4].label}</p>
                                                                <p className='flex-1'>
                                                                    {convertStatus(item.status)}
                                                                </p>
                                                            </div>
                                                            <div className='flex flex-[2.0] tablet:order-1'>
                                                                <div className={`flex-[1.0] ${tBodyStyle()} flex-col tablet:truncate`}>
                                                                    <span className={titleStyle()}>{tHeader[5].label}</span>
                                                                    <p className='text-lg flag'>{countryCodeToEmoji(item.originCode.slice(0, 2))}</p>
                                                                    <p dangerouslySetInnerHTML={{ __html: pointCountry(item.originCode) }}></p>
                                                                    <p>{item.originAddress}</p>
                                                                </div>
                                                                <div className={`flex-[1.0] ${tBodyStyle()} flex-col tablet:truncate`}>
                                                                    <span className={titleStyle()}>{tHeader[6].label}</span>
                                                                    <p className='text-lg flag'>{countryCodeToEmoji(item.destinationCode.slice(0, 2))}</p>
                                                                    <p dangerouslySetInnerHTML={{ __html: pointCountry(item.destinationCode) }}></p>
                                                                    <p>{item.destinationAddress}</p>
                                                                </div>
                                                            </div>
                                                            <div className={`flex-[1.5] ${tBodyStyle()} flex-col tablet:order-7`}>
                                                                <p className={titleStyle()}>{tHeader[7].label}</p>
                                                                {
                                                                    (item.containerInfo && item.containerInfo.length > 0) ?
                                                                        item.containerInfo.map((item, idx) => (
                                                                            <p key={idx}>
                                                                                {item.containerType} {item.containerWeight}KG * {item.containerQuantity}
                                                                            </p>
                                                                        )) :
                                                                        '-'
                                                                }
                                                            </div>

                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        :
                                        <div className='flex justify-center items-center py-6 min-h-[30vh]'>
                                            데이터가 없습니다.
                                        </div>
                                }
                            </div>
                        </div>
                    </div>

                    <Pagination
                        totalPage={totalPage}
                        currentPage={searchInfo.page}
                        onClick={(idx) => {
                            setSearchInfo(p => ({
                                ...p,
                                page: (idx + 1)
                            }))
                        }}
                    />
                </div>
            </Container>
        </PageTemplate>
    )
}

export default StatusList